import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, targetsAPI } from '../utils/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Target, Activity as ActivityIcon, Users, AlertTriangle, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tables, setTables] = useState(null);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [kpisRes, chartsRes, tablesRes, targetsRes] = await Promise.all([
        dashboardAPI.getKPIs({ year: selectedYear }),
        dashboardAPI.getCharts({ year: selectedYear }),
        dashboardAPI.getTables({ year: selectedYear }),
        targetsAPI.getByYear(selectedYear)
      ]);
      
      setKpis(kpisRes.data);
      setCharts(chartsRes.data);
      setTables(tablesRes.data);
      setTargets(targetsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value) => {
    return `RM ${(value / 1000).toFixed(0)}K`;
  };

  const formatCurrencyFull = (value) => {
    return `RM ${(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0 })}`;
  };

  const COLORS = ['#2563EB', '#10B981', '#0EA5E9', '#F59E0B', '#EF4444'];

  // Calculate variance percentages
  const wonValue = kpis?.won_ytd?.value || 0;
  const minimumVariance = targets?.minimum_target ? ((wonValue - targets.minimum_target) / targets.minimum_target * 100) : 0;
  const setTargetVariance = targets?.set_target ? ((wonValue - targets.set_target) / targets.set_target * 100) : 0;
  const stretchVariance = targets?.stretch_target ? ((wonValue - targets.stretch_target) / targets.stretch_target * 100) : 0;

  // Determine status
  const getTargetStatus = () => {
    if (wonValue >= (targets?.stretch_target || 0)) return { status: 'exceeded', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    if (wonValue >= (targets?.set_target || 0)) return { status: 'on-track', color: 'text-blue-600', bg: 'bg-blue-50', icon: CheckCircle };
    if (wonValue >= (targets?.minimum_target || 0)) return { status: 'minimum', color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertTriangle };
    return { status: 'below', color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle };
  };

  const targetStatus = getTargetStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Business development overview</p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="dashboard-year-select"
        >
          <option value={2022}>2022</option>
          <option value={2023}>2023</option>
          <option value={2024}>2024</option>
          <option value={2025}>2025</option>
          <option value={2026}>2026</option>
        </select>
      </div>

      {/* Target Variance Section */}
      {targets && (targets.minimum_target > 0 || targets.set_target > 0 || targets.stretch_target > 0) && (
        <div className={`${targetStatus.bg} rounded-lg border p-6`} data-testid="target-variance-section">
          <div className="flex items-center gap-3 mb-4">
            <targetStatus.icon className={targetStatus.color} size={24} />
            <h2 className="text-lg font-semibold text-slate-900">
              {selectedYear} Target Performance
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Won Value */}
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="text-sm text-slate-600 mb-1">Won Value (YTD)</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">{formatCurrencyFull(wonValue)}</p>
            </div>

            {/* Minimum Target */}
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="text-sm text-slate-600 mb-1">vs Minimum Target</p>
              <p className="text-lg font-semibold text-slate-700">{formatCurrencyFull(targets.minimum_target)}</p>
              <div className={`flex items-center gap-1 mt-1 text-sm ${minimumVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {minimumVariance >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span>{minimumVariance >= 0 ? '+' : ''}{minimumVariance.toFixed(1)}%</span>
                <span className="text-slate-500">({formatCurrency(wonValue - targets.minimum_target)})</span>
              </div>
            </div>

            {/* Set Target */}
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="text-sm text-slate-600 mb-1">vs Set Target</p>
              <p className="text-lg font-semibold text-slate-700">{formatCurrencyFull(targets.set_target)}</p>
              <div className={`flex items-center gap-1 mt-1 text-sm ${setTargetVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {setTargetVariance >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span>{setTargetVariance >= 0 ? '+' : ''}{setTargetVariance.toFixed(1)}%</span>
                <span className="text-slate-500">({formatCurrency(wonValue - targets.set_target)})</span>
              </div>
            </div>

            {/* Stretch Target */}
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="text-sm text-slate-600 mb-1">vs Stretch Target</p>
              <p className="text-lg font-semibold text-slate-700">{formatCurrencyFull(targets.stretch_target)}</p>
              <div className={`flex items-center gap-1 mt-1 text-sm ${stretchVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stretchVariance >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span>{stretchVariance >= 0 ? '+' : ''}{stretchVariance.toFixed(1)}%</span>
                <span className="text-slate-500">({formatCurrency(wonValue - targets.stretch_target)})</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Progress to Stretch Target</span>
              <span>{((wonValue / (targets.stretch_target || 1)) * 100).toFixed(1)}%</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 via-amber-500 via-blue-500 to-green-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (wonValue / (targets.stretch_target || 1)) * 100)}%` }}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-between text-xs mt-1 gap-0.5">
              <span className="text-red-600">Min: {formatCurrency(targets.minimum_target)}</span>
              <span className="text-blue-600">Set: {formatCurrency(targets.set_target)}</span>
              <span className="text-green-600">Stretch: {formatCurrency(targets.stretch_target)}</span>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Pipeline Value"
          value={formatCurrency(kpis?.total_pipeline?.value || 0)}
          delta={kpis?.total_pipeline?.delta || 0}
          positive={kpis?.total_pipeline?.positive}
          icon={DollarSign}
          testId="kpi-total-pipeline-card"
        />
        <KPICard
          title="Won Value (YTD)"
          value={formatCurrency(kpis?.won_ytd?.value || 0)}
          delta={kpis?.won_ytd?.delta || 0}
          positive={kpis?.won_ytd?.positive}
          icon={Target}
          testId="kpi-won-ytd-card"
        />
        <KPICard
          title="Win Rate"
          value={`${kpis?.win_rate?.value || 0}%`}
          delta={kpis?.win_rate?.delta || 0}
          positive={kpis?.win_rate?.positive}
          icon={ActivityIcon}
          testId="kpi-win-rate-card"
        />
        <KPICard
          title="Weighted Pipeline"
          value={formatCurrency(kpis?.weighted_pipeline?.value || 0)}
          delta={kpis?.weighted_pipeline?.delta || 0}
          positive={kpis?.weighted_pipeline?.positive}
          icon={Users}
          testId="kpi-weighted-pipeline-card"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline by Stage */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Pipeline by Stage</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={charts?.pipeline_by_stage || []}>
              <XAxis dataKey="stage" />
              <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="value" fill="#2563EB" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Pillar */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Revenue by Pillar</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={charts?.revenue_by_pillar || []}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={(entry) => entry.pillar}
              >
                {(charts?.revenue_by_pillar || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline by Partner */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Pipeline by Partner</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600">Partner</th>
                  <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">Deals</th>
                  <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">Value</th>
                </tr>
              </thead>
              <tbody>
                {(tables?.pipeline_by_partner || []).map((partner) => (
                  <tr key={partner.partner_name || partner._id} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-sm text-slate-900">{partner.partner_name}</td>
                    <td className="py-2 px-3 text-sm text-slate-900 text-right">{partner.deal_count}</td>
                    <td className="py-2 px-3 text-sm text-slate-900 text-right">{formatCurrency(partner.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Recent Activities</h3>
          <div className="space-y-3">
            {(tables?.recent_activities || []).map((activity) => (
              <div key={activity._id || activity.subject + activity.client_name} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{activity.subject}</p>
                  <p className="text-xs text-slate-500 mt-1">{activity.client_name} • {activity.logged_by_name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const KPICard = ({ title, value, delta, positive, icon: Icon, testId }) => {
  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 sm:p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs sm:text-sm text-slate-600">{title}</span>
        <div className={`flex items-center gap-1 text-xs sm:text-sm ${
          positive ? 'text-green-600' : 'text-red-600'
        }`}>
          {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{Math.abs(delta)}%</span>
        </div>
      </div>
      <div className="text-2xl sm:text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
};

export default Dashboard;
